import { Heatmap } from '@ant-design/plots'
import { Empty } from 'antd'


const GameUsageHeatmap = ({
  data,
  theme = 'classicDark',
  chartHeight = 300,
  chartWidth = 670,
  axisVisibility = 'day_hour', 
}) => {
  const mode = axisVisibility 
  const isDataForMode = data?.meta?.heatmap_mode === mode;

  const serverHeatmap = Array.isArray(data?.heatmap) ? data.heatmap : []
  const dayOrder = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

  const dayHourData = mode === 'day_hour'
    ? serverHeatmap
        .filter(d => typeof d.day === 'string' && Number.isFinite(d.hour))
        .map(d => ({ day: d.day, hour: Number(d.hour), count: Number(d.count) || 0 }))
        .sort((a, b) => {
          const di = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day)
          return di !== 0 ? di : a.hour - b.hour
        })
    : []

  const dayData = mode === 'day'
    ? serverHeatmap
        .filter(d => typeof d.day === 'string')
        .map(d => ({ day: d.day, count: Number(d.count) || 0 }))
        .sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day))
    : []

  const hourData = mode === 'hour'
    ? serverHeatmap
        .filter(d => Number.isFinite(d.hour))
        .map(d => ({ hour: Number(d.hour), count: Number(d.count) || 0 }))
        .sort((a, b) => a.hour - b.hour)
    : []

  const displayEmpty =
    (mode === 'day_hour' && dayHourData.length === 0) ||
    (mode === 'day' && dayData.length === 0) ||
    (mode === 'hour' && hourData.length === 0)

  if (displayEmpty) {
    return (
      <div
        style={{
          height: chartHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme === 'classicDark' ? '#141414' : '#f5f5f5',
        }}
      >
        <Empty description="No Data Available" />
      </div>
    )
  }

  const colorPalette = [
    'rgba(179, 218, 251, 0.3)',
    'rgba(137, 194, 245, 0.4)',
    'rgba(103, 173, 238, 0.5)',
    'rgba(74, 152, 230, 0.6)',
    'rgba(59, 136, 217, 0.7)',
    'rgba(51, 114, 184, 0.8)',
    'rgba(43, 91, 146, 0.9)',
    'rgba(34, 69, 111, 1)',
    'rgba(23, 51, 84, 1)',
    'rgba(12, 29, 47, 1)',
  ]

  const values = arr => (arr.length ? arr.map(d => d.count) : [0])
  const minMax = arr => {
    const v = values(arr)
    return { min: Math.min(...v), max: Math.max(...v) }
  }

  const dayHourMinMax = minMax(dayHourData)
  const dayMinMax = minMax(dayData)
  const hourMinMax = minMax(hourData)

  const fmtHour = (h) => {
    const n = Number(h)
    if (n === 0) return '12AM'
    if (n === 12) return '12PM'
    return n < 12 ? `${n}AM` : `${n - 12}PM`
  }
  const tickHours = [0, 6, 12, 18]

  const baseStyle = {
    lineWidth: 2,
    stroke: theme === 'classicDark' ? '#141414' : '#f5f5f5',
    opacity: 0.8,
    shadowColor: 'rgba(0, 0, 0, 0.2)',
    shadowBlur: 5,
  }

  const dayHourConfig = {
    theme,
    height: chartHeight,
    width: chartWidth,
    autoFit: false,
    data: dayHourData,
    xField: 'hour',
    yField: 'day',
    colorField: 'count',
    mark: 'cell',
    axis: {
      x: {
        grid: null,
        line: null,
        tick: null,
        label: {
          style: { fill: '#aaa' },
          formatter: (val) => (tickHours.includes(Number(val)) ? fmtHour(val) : ''),
        },
      },
      y: { grid: null, line: null, tick: null, label: { style: { fill: '#aaa' } } },
    },
    tooltip: {
      items: [
        { field: 'count', name: 'Count' },
        { field: 'hour', name: 'Hour', formatter: fmtHour },
        { field: 'day', name: 'Day' },
      ],
    },
    color: colorPalette,
    legend: false,
    style: baseStyle,
    scale: {
      x: { domain: Array.from({ length: 24 }, (_, i) => i) },
      y: { domain: dayOrder },
      color: {
        type: 'quantize',
        domain: [dayHourMinMax.min, dayHourMinMax.max],
        range: colorPalette,
      },
    },
  }

  const dayConfig = {
    theme,
    height: chartHeight,
    width: chartWidth,
    autoFit: false,
    data: dayData,
    yField: 'day',
    colorField: 'count',
    mark: 'cell',
    tooltip: { items: [{ field: 'count', name: 'Count' }, { field: 'day', name: 'Day' }] },
    axis: {
      y: { grid: null, line: null, tick: null, label: { style: { fill: '#aaa' } } },
    },
    color: colorPalette,
    legend: false,
    style: baseStyle,
    scale: {
      y: { domain: dayOrder },
      color: {
        type: 'quantize',
        domain: [dayMinMax.min, dayMinMax.max],
        range: colorPalette,
      },
    },
  }

  const hourConfig = {
    theme,
    height: chartHeight,
    width: chartWidth,
    autoFit: false,
    data: hourData,
    xField: 'hour',
    colorField: 'count',
    mark: 'cell',
    tooltip: {
      items: [
        { field: 'count', name: 'Count' },
        { field: 'hour', name: 'Hour', formatter: fmtHour },
      ],
    },
    axis: {
      x: {
        grid: null,
        line: null,
        tick: null,
        label: {
          style: { fill: '#aaa' },
          formatter: (val) => (tickHours.includes(Number(val)) ? fmtHour(val) : ''),
        },
      },
    },
    color: colorPalette,
    legend: false,
    style: baseStyle,
    scale: {
      x: { domain: Array.from({ length: 24 }, (_, i) => i) },
      color: {
        type: 'quantize',
        domain: [hourMinMax.min, hourMinMax.max],
        range: colorPalette,
      },
    },
  }

  const finalConfig = mode === 'day_hour' ? dayHourConfig : mode === 'hour' ? hourConfig : dayConfig

  return <Heatmap 
  key={`${mode}-${isDataForMode ? serverHeatmap.length : 0}`}
  {...finalConfig} />
}

export default GameUsageHeatmap
